import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { useEffect, useRef } from "react";
import blueMarker from "../Assets/blueMarker.svg";
import blueCluster from "../Assets/blueCluster.svg";
import { isValidArray, isValidObject } from "../Services/validators";
import { setTrackingLoading } from "../Redux/track/action";
import {
  dateAndTimeConverter,
  generateAestheticColor
} from "../Utils/Constants/Constants";

export default function useMap(props) {
  const map = useRef({
    Map: null,
    AdvancedMarkerElement: null,
    loading: false
  });
  const markers = useRef({});
  const colorRef = useRef({});
  const clustered = useRef(null);
  const isInitialized = useRef(false);
  const polylineRef = useRef([]);
  const markerRef = useRef([]);
  const customOverlayRef = useRef([]);

  useEffect(() => {
    if (
      props.isAuth &&
      props.mapContainerRendered &&
      !map.current.Map &&
      !map.current.loading
    ) {
      loadMapsAndMarkers(props.container.current);
    }
    // eslint-disable-next-line
  }, [props.isAuth, props.mapContainerRendered]);

  useEffect(() => {
    if (isInitialized.current) {
      markLocations();
    }
    // eslint-disable-next-line
  }, [props.locations]);

  const clearRoutes = () => {
    polylineRef.current.forEach((display) => {
      display.setMap(null);
    });
    markerRef.current.forEach((display) => {
      display.start.setMap(null);
      display.end.setMap(null);
    });
    polylineRef.current = [];
    markerRef.current = [];
  };

  const drawRoutesInMap = (tracks, employees) => {
    if (!isInitialized.current) {
      return;
    }

    clearRoutes();
    if (isValidObject(tracks) && isValidObject(employees)) {
      console.log("inside drawRoutesInMap");
      const employeeData = Object.values(employees);
      setTrackingLoading(true);

      Object.entries(tracks).forEach(([key, { tracks: trackLocations }]) => {
        const phoneNumber = `+91${key}`;
        const employee = employeeData.find(
          (data) => data.phoneNumber === phoneNumber
        );

        if (employee) {
          if (!colorRef.current[phoneNumber]) {
            colorRef.current[phoneNumber] = generateAestheticColor();
          }

          const color = colorRef.current[phoneNumber];
          initializeMap(
            trackLocations,
            color,
            employeeData.find((data) => data.phoneNumber === `+91${key}`)
          );
        }
      });
    }
  };

  const loadMapsAndMarkers = async (mapContainer) => {
    if (map.current.loading || isInitialized.current) {
      return;
    }

    // eslint-disable-next-line no-undef
    const { Map } = await google.maps.importLibrary("maps");
    // eslint-disable-next-line no-undef
    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");

    map.current.loading = true;
    const mapOptions = {
      center: { lat: 13.048129476915838, lng: 80.2305531172708 },
      disableDefaultUI: true,
      fullscreenControl: false,
      mapId: props.theme === "dark" ? "ec89d2da8424be59" : "d7d94c875bbf8a73",
      mapTypeControl: false,
      mapTypeControlOptions: false,
      mapTypeId: window.google.maps.MapTypeId?.ROADMAP,
      minZoom: 8,
      panControl: false,
      scaleControl: false,
      scrollwheel: true,
      streetViewControl: false,
      zoom: 12,
      zoomControl: false
    };

    map.current.Map = new Map(mapContainer, mapOptions);
    map.current.AdvancedMarkerElement = AdvancedMarkerElement;
    map.current.loading = false;
    isInitialized.current = true;

    markLocations();
  };

  const initializeMap = async (locations, color, userData) => {
    if (!locations || locations.length === 0) {
      clearRoutes();
      setTrackingLoading(false);
      return;
    }

    const wayPts = [];
    const coordinates = [];
    const bounds = new window.google.maps.LatLngBounds();

    for (let i = 0; i < locations?.length; i++) {
      const lat = parseFloat(locations[i].lat);
      const lng = parseFloat(locations[i].lon);
      if (!isNaN(lat) && !isNaN(lng)) {
        coordinates.push({
          lat,
          lng,
          data: locations[i]
        });
        wayPts.push({
          lat,
          lng
        });
        bounds.extend({ lat, lng });
      }
    }

    if (isValidArray(wayPts)) {
      const optimizedWaypoints = await snapToRoad(wayPts);
      drawSnappedPolyline(optimizedWaypoints, map, {
        color: color,
        userData: userData,
        start: {
          lat: coordinates[0].lat,
          lng: coordinates[0].lng,
          data: coordinates[0].data
        },
        end: {
          lat: coordinates[coordinates.length - 1].lat,
          lng: coordinates[coordinates.length - 1].lng,
          data: coordinates[coordinates.length - 1].data
        }
      });
      // map.current.Map.fitBounds(overallBounds);
      setTrackingLoading(false);
    } else {
      setTrackingLoading(false);
    }
  };

  const markLocations = () => {
    if (!isValidArray(props.locations) || !map.current.Map) {
      return;
    }
    const infoWindow = new window.google.maps.InfoWindow();

    // check because of this memory increases or not
    // Object.values(markers.current).forEach((marker) => marker.setMap(null));
    // markers.current = {};

    const newMarkers = props.locations
      .map((data) => {
        if (!data || markers.current[data.documentId]) {
          return null;
        }

        const imageElement = document.createElement("img");
        imageElement?.setAttribute("src", blueMarker);

        const marker = new map.current.AdvancedMarkerElement({
          map: map.current.Map,
          position: {
            lat: data._geoloc?.lat,
            lng: data._geoloc?.lng
          },
          title: `${data?.PCTNo?.toString()?.padStart(2, "0")} - ${
            data?.location
          }`,
          content: imageElement,
          zIndex: 1
        });

        marker.addListener("gmp-click", () => {
          infoWindow.close();
          infoWindow.setContent(createInfoWindowContent(data));
          infoWindow.open(map.current.Map, marker);
        });

        markers.current[data.documentId] = marker;
        return marker;
      })
      .filter(Boolean);

    setupCluster(newMarkers);
    setupInfoWindowListener(infoWindow);
    map.current.Map.addListener("click", () => {
      infoWindow.close();
    });
  };

  const setupCluster = (newMarkers) => {
    if (!newMarkers || newMarkers.length === 0) {
      return;
    }

    if (!clustered.current) {
      clustered.current = new MarkerClusterer({
        map: map.current.Map,
        markers: newMarkers,
        renderer: {
          render({ count, position }) {
            const containerElement = document.createElement("div");
            containerElement.style.position = "relative";
            containerElement.style.display = "inline-block";
            containerElement.style.width = "24px";
            containerElement.style.height = "24px";

            const imageElement = document.createElement("img");
            imageElement.setAttribute("src", blueCluster);
            imageElement.style.width = "100%";
            imageElement.style.height = "100%";
            imageElement.style.transform = `scale(${Math.min(
              1.2,
              1 + count / 8
            )})`;

            const countElement = document.createElement("span");
            countElement.textContent = count;
            countElement.style.position = "absolute";
            countElement.style.top = "50%";
            countElement.style.left = "50%";
            countElement.style.transform = "translate(-50%, -50%)";
            countElement.style.color = "#C4c4c4";
            countElement.style.fontSize = "8px";
            countElement.style.zIndex = "2";

            containerElement.appendChild(imageElement);
            containerElement.appendChild(countElement);

            return new map.current.AdvancedMarkerElement({
              position,
              content: containerElement,
              title: `${count}`,
              zIndex: 1
            });
          }
        }
      });
    } else {
      clustered.current.clearMarkers();
      clustered.current.addMarkers(newMarkers);
    }
  };

  const setupInfoWindowListener = (infoWindow) => {
    window.google.maps.event.addListener(infoWindow, "domready", () => {
      const customClickElement = document.getElementById("custom-click");
      if (customClickElement) {
        customClickElement.addEventListener("click", () => {
          props.markerOnclick(customClickElement.getAttribute("name"));
        });
      }
    });
  };

  function drawSnappedPolyline(wayPoints, map, data) {
    let currentPolylinePath = [];
    let lastPoint = null;
    let markers = null;

    for (let i = 0; i < wayPoints?.length; i++) {
      const currentCoord = wayPoints[i];

      if (!lastPoint) {
        currentPolylinePath.push(currentCoord);
        lastPoint = currentCoord;
      } else {
        const distance = haversineDistance(lastPoint, currentCoord);
        if (distance > 500) {
          if (currentPolylinePath.length > 1) {
            markers = createRouteMarkers(
              currentPolylinePath[0],
              currentPolylinePath[currentPolylinePath.length - 1],
              map.current.Map,
              map.current.AdvancedMarkerElement
            );
            const polyline = new window.google.maps.Polyline({
              path: currentPolylinePath,
              geodesic: true,
              strokeColor: data.color,
              strokeOpacity: 1.0,
              strokeWeight: 4,
              padding: 4,
              zIndex: 1,
              icons: [
                {
                  icon: {
                    path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                    scale: 2,
                    strokeColor: "#FFFFF"
                  },
                  offset: "100%",
                  repeat: "200px"
                }
              ]
            });

            polyline.setMap(map.current.Map);
            if (polyline) {
              let customOverlay;
              polyline.addListener("mouseover", (event) => {
                polyline.setOptions({ zIndex: 10 });
                customOverlay = createCustomOverlay(
                  map.current.Map,
                  event.latLng,
                  content(
                    data.userData.name,
                    data.userData.phoneNumber,
                    data.start.data.timestamp
                  )
                );
                customOverlayRef.current.push(customOverlay);
              });

              polyline.addListener("mouseout", () => {
                if (customOverlay) {
                  customOverlay.onRemove();
                  customOverlayRef.current = [];
                }
              });

              polylineRef.current.push(polyline);
            }
            markerRef.current.push(markers);
          }
          currentPolylinePath = [currentCoord];
        } else {
          currentPolylinePath.push(currentCoord);
        }
        lastPoint = currentCoord;
      }
    }

    if (currentPolylinePath.length > 1) {
      markers = createRouteMarkers(
        currentPolylinePath[0],
        currentPolylinePath[currentPolylinePath.length - 1],
        map.current.Map,
        map.current.AdvancedMarkerElement
      );
      const polyline = new window.google.maps.Polyline({
        path: currentPolylinePath,
        geodesic: true,
        strokeColor: data.color,
        strokeOpacity: 1.0,
        strokeWeight: 4,
        padding: 4,
        zIndex: 1,
        icons: [
          {
            icon: {
              path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
              scale: 2,
              strokeColor: "#FFFFF"
            },
            offset: "100%",
            repeat: "200px"
          }
        ]
      });

      polyline.setMap(map.current.Map);
      if (polyline) {
        let customOverlay;
        polyline.addListener("mouseover", (event) => {
          polyline.setOptions({ zIndex: 10 });
          customOverlay = createCustomOverlay(
            map.current.Map,
            event.latLng,
            content(
              data.userData.name,
              data.userData.phoneNumber,
              data.start.data.timestamp
            )
          );
          customOverlayRef.current.push(customOverlay);
        });

        polyline.addListener("mouseout", () => {
          if (customOverlay) {
            customOverlay.onRemove();
            customOverlayRef.current = [];
          }
        });
        polylineRef.current.push(polyline);
      }
      markerRef.current.push(markers);
      setTrackingLoading(false);
    }
    setTrackingLoading(false);
  }

  const createRouteMarkers = (start, end) => {
    const startIconElement = document.createElement("div");
    startIconElement.style.zIndex = "200";
    startIconElement.innerHTML = `<img src="${window.location.origin}/Arrival-Green.svg" width="14" height="14" alt="Start Icon">`;

    const endIconElement = document.createElement("div");
    endIconElement.style.zIndex = "200";
    endIconElement.innerHTML = `<img src="${window.location.origin}/Departure-Red.svg" width="14" height="14" alt="End Icon">`;

    const startMaker = new map.current.AdvancedMarkerElement({
      position: start,
      map: map.current.Map,
      content: startIconElement,
      zIndex: 1,
      title: "Starting Point"
    });

    const endMarker = new map.current.AdvancedMarkerElement({
      position: end,
      map: map.current.Map,
      content: endIconElement,
      zIndex: 1,
      title: "Ending Point"
    });
    return { start: startMaker, end: endMarker };
  };
  return [drawRoutesInMap, clearRoutes];
}

const createInfoWindowContent = (data) => {
  return `<div class="font-color-tertiaryColor" style="display:flex;z-index:1000;font-size:12px;font-family:switzer-variable;padding:16px;">
      <div>
        <div style="height:100px;width:100px;background-color:#262626;display:flex;justify-content:center;align-items:center;">
          <svg
            width="24"
            height="24"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14 0.707L13.293 0L0 13.293L0.707 14L1.707 13H12C12.2651 12.9996 12.5192 12.8941 12.7067 12.7067C12.8941 12.5192 12.9996 12.2651 13 12V1.707L14 0.707ZM12 12H2.707L6.6035 8.1035L7.793 9.293C7.98053 9.48047 8.23484 9.58579 8.5 9.58579C8.76516 9.58579 9.01947 9.48047 9.207 9.293L10 8.5L12 10.4985V12ZM12 9.084L10.707 7.791C10.5195 7.60353 10.2652 7.49821 10 7.49821C9.73484 7.49821 9.48053 7.60353 9.293 7.791L8.5 8.584L7.3115 7.3955L12 2.707V9.084ZM2 10V8.5L4.5 6.0015L5.1865 6.6885L5.8945 5.9805L5.207 5.293C5.01947 5.10553 4.76516 5.00021 4.5 5.00021C4.23484 5.00021 3.98053 5.10553 3.793 5.293L2 7.086V2H10V1H2C1.73486 1.00026 1.48066 1.10571 1.29319 1.29319C1.10571 1.48066 1.00026 1.73486 1 2V10H2Z"
              fill="white"
            />
          </svg>
        </div>
      </div>
      <div style="padding-left:16px;width:100%">
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <div style="font-size:14px;text-transform:uppercase;white-space:nowrap;padding-right:64px;">
            ${data?.location || "No ID Available"}
          </div>
          <div
            id="custom-click"
            name="${data.documentId}"
            style="cursor:pointer;width:12px;height:12px;display:none"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M1 1H9.6V2.2H2.2V13.8H13.8V6.4H15V15H1V1Z"
                fill="var(--tertiaryColor6)"
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M10.8966 1.09712L14.8907 1.09712V5.09123H13.6907V3.18815L9.43822 7.61638L8.5727 6.7852L12.8827 2.29712L10.8966 2.29712V1.09712Z"
                fill="var(--tertiaryColor6)"
              />
            </svg>
          </div>
        </div>
        <div style="height:1px;width:100%;background-color:#FFFFFF14;margin-top:8px"></div>
        <div style="padding-top:8px;font-size:10px;justify-content:space-between;display:flex;flex-direction:column;height:60px">
          <div style="display:flex;justify-content:space-between;margin-bottom:12px;">
            <span>PCT #</span>
            <span>${data?.PCTNo}</span>
          </div>

          <div style="display:flex;justify-content:space-between;margin-bottom:12px;">
            <span>ZONE</span>
            <span>${data?.zone}</span>
          </div>

          <div style="display:flex;justify-content:space-between;">
            <span>WARD</span>
            <span>${data?.ward}</span>
          </div>
        </div>
      </div>
    </div>`;
};

const createCustomOverlay = (map, position, content) => {
  let div = null;

  const overlay = new window.google.maps.OverlayView();

  overlay.onAdd = () => {
    div = document.createElement("div");
    div.style.position = "absolute";
    div.style.width = "240px";
    div.style.height = "fit-content";
    div.style.zIndex = "1000";
    div.style.padding = "16px";
    div.style.color = "white";
    div.style.backgroundColor = "#020d23";
    div.style.border = "solid 1px grey";

    div.innerHTML = content;

    const panes = overlay.getPanes();
    panes.overlayMouseTarget.appendChild(div);
  };

  overlay.draw = () => {
    const overlayProjection = overlay.getProjection();
    const pixelPosition = overlayProjection.fromLatLngToDivPixel(position);

    if (div) {
      div.style.left = `${pixelPosition.x}px`;
      div.style.top = `${pixelPosition.y}px`;
    }
  };

  overlay.onRemove = () => {
    if (div) {
      div.parentNode.removeChild(div);
      div = null;
    }
  };

  overlay.setMap(map);
  return overlay;
};

function haversineDistance(coord1, coord2) {
  const R = 6371e3;
  const toRadians = (degrees) => (degrees * Math.PI) / 180;

  const lat1 = toRadians(coord1.lat);
  const lat2 = toRadians(coord2.lat);
  const deltaLat = toRadians(coord2.lat - coord1.lat);
  const deltaLon = toRadians(coord2.lng - coord1.lng);

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) *
      Math.cos(lat2) *
      Math.sin(deltaLon / 2) *
      Math.sin(deltaLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function isolateFarCoordinates(coordinates, maxDistance) {
  const filteredCoordinates = [];

  for (let i = 0; i < coordinates.length; i++) {
    const prevCoord = coordinates[i - 1] || null;
    const currentCoord = coordinates[i];
    const nextCoord = coordinates[i + 1] || null;

    const prevDistance = prevCoord
      ? haversineDistance(currentCoord, prevCoord)
      : 0;
    const nextDistance = nextCoord
      ? haversineDistance(currentCoord, nextCoord)
      : 0;

    if (
      (prevCoord === null || prevDistance <= maxDistance) &&
      (nextCoord === null || nextDistance <= maxDistance)
    ) {
      filteredCoordinates.push(currentCoord);
    }
  }

  return filteredCoordinates;
}

const snapToRoad = async (path) => {
  try {
    const snappedCoordinates = [];
    const filteredCoordinates = [];
    let lastPoint = null;

    isolateFarCoordinates(path, 200).forEach((point) => {
      if (!lastPoint || haversineDistance(lastPoint, point) >= 20) {
        filteredCoordinates.push(point);
        lastPoint = point;
      }
    });

    const batchSize = 100;
    const batches = [];
    for (let i = 0; i < filteredCoordinates.length; i += batchSize) {
      batches.push(filteredCoordinates.slice(i, i + batchSize));
    }

    for (const batch of batches) {
      const path = batch.map((point) => `${point.lat},${point.lng}`).join("|");

      const url = `https://roads.googleapis.com/v1/snapToRoads?path=${path}&interpolate=true&key=${process.env.REACT_APP_MAP_KEY}`;

      const response = await fetch(url);
      const data = await response.json();
      console.log("Made ", batches.length, " no. of calls");
      if (data.snappedPoints) {
        snappedCoordinates.push(
          ...data.snappedPoints.map((point) => ({
            lat: point.location.latitude,
            lng: point.location.longitude
          }))
        );
      } else {
        throw new Error("No snapped points found");
      }
    }
    return snappedCoordinates;
  } catch (error) {
    throw error;
  }
};

const content = (name, phoneNumber, timestamp) => {
  return `<div style="z-index:1000;font-family:aldrich-regular;">
      <div style="width: 4px; height: 4px; background-color: white; position: absolute; top: -1px; left: -1px;"></div>
      <div style="width: 4px; height: 4px; background-color: white; position: absolute; top: -1px; right: -1px;"></div>
      <div style="width: 4px; height: 4px; background-color: white; position: absolute; bottom: -1px; left: -1px;"></div>
      <div style="width: 4px; height: 4px; background-color: white; position: absolute; bottom: -1px; right: -1px;"></div>
      <div style="display:flex">
        <div style="padding-right:8px">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="24" height="24" fill="#565656" fill-opacity="0.3" />
            <path
              d="M19.8948 18.5919C18.8792 16.7442 17.2933 15.3045 15.3924 14.5045C16.3377 13.7696 17.0359 12.745 17.3882 11.5759C17.7406 10.4068 17.7291 9.15238 17.3554 7.99039C16.9818 6.8284 16.2649 5.81774 15.3063 5.10156C14.3477 4.38538 13.1961 4 12.0144 4C10.8328 4 9.68116 4.38538 8.72258 5.10156C7.76401 5.81774 7.04711 6.8284 6.67345 7.99039C6.29979 9.15238 6.28831 10.4068 6.64063 11.5759C6.99295 12.745 7.69121 13.7696 8.6365 14.5045C6.73562 15.3045 5.14971 16.7442 4.13407 18.5919C4.07114 18.6968 4.0292 18.8138 4.01073 18.9358C3.99227 19.0578 3.99767 19.1825 4.0266 19.3023C4.05554 19.4221 4.10742 19.5347 4.17916 19.6333C4.25091 19.7319 4.34105 19.8146 4.44423 19.8764C4.54741 19.9382 4.66153 19.9779 4.77979 19.993C4.89805 20.0082 5.01805 19.9986 5.13265 19.9647C5.24725 19.9308 5.35411 19.8734 5.44688 19.7959C5.53965 19.7184 5.61643 19.6223 5.67266 19.5134C7.01495 17.1084 9.38543 15.674 12.0144 15.674C14.6434 15.674 17.0139 17.1092 18.3562 19.5134C18.4781 19.7165 18.6714 19.8625 18.8952 19.9206C19.1191 19.9787 19.356 19.9443 19.5558 19.8246C19.7556 19.7049 19.9027 19.5094 19.9659 19.2794C20.0292 19.0493 20.0037 18.8028 19.8948 18.5919ZM8.16241 9.83815C8.16241 9.04842 8.38832 8.27643 8.81159 7.61979C9.23486 6.96315 9.83646 6.45136 10.5403 6.14915C11.2442 5.84693 12.0187 5.76786 12.7659 5.92193C13.5132 6.076 14.1995 6.45629 14.7382 7.01471C15.2769 7.57313 15.6438 8.28461 15.7925 9.05917C15.9411 9.83372 15.8648 10.6366 15.5732 11.3662C15.2817 12.0958 14.788 12.7194 14.1545 13.1582C13.521 13.5969 12.7763 13.8311 12.0144 13.8311C10.9932 13.8299 10.0141 13.4088 9.29194 12.6602C8.5698 11.9117 8.16358 10.8968 8.16241 9.83815Z"
              fill="white"
            />
          </svg>
        </div>
        <div style="padding-bottom:16px">
          <div style="text-transform:uppercase;font-size:10px;">${name}</div>
          <div style="padding-top:4px;font-size:14px;">${phoneNumber}</div>
        </div>
      </div>

      <div style="display: flex; justify-content: space-between;">
        <div style="display: flex; align-items: center; flex-direction: column;">
          <div style="font-size:10px;">TIME</div>
          <div style="padding-top:8px;">
            ${dateAndTimeConverter(timestamp, "Time")}
          </div>
        </div>
        <div style="display: flex; align-items: center; flex-direction: column;">
          <div style="font-size:10px;" >DIRECTION</div>

          <div style="transform: rotate(180deg);padding-top:8px;">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.4564 7.5L2.02231 7.5C1.88394 7.5 1.75125 7.55267 1.65341 7.64643C1.55557 7.74018 1.50061 7.86735 1.50061 7.99994C1.50061 8.13253 1.55557 8.25969 1.65341 8.35345C1.75125 8.4472 1.88394 8.49987 2.02231 8.49987H12.4564C12.5947 8.49987 12.7274 8.4472 12.8253 8.35345C12.9231 8.25969 12.9781 8.13253 12.9781 7.99994C12.9781 7.86735 12.9231 7.74018 12.8253 7.64643C12.7274 7.55267 12.5947 7.5 12.4564 7.5Z"
                fill="white"
              />
              <path
                d="M12.2405 8L7.91349 12.1455C7.81553 12.2394 7.76049 12.3667 7.76049 12.4994C7.76049 12.6322 7.81553 12.7595 7.91349 12.8534C8.01145 12.9473 8.14431 13 8.28285 13C8.42139 13 8.55426 12.9473 8.65222 12.8534L13.3475 8.35396C13.3961 8.30752 13.4347 8.25235 13.461 8.19161C13.4873 8.13087 13.5008 8.06576 13.5008 8C13.5008 7.93424 13.4873 7.86913 13.461 7.80839C13.4347 7.74765 13.3961 7.69248 13.3475 7.64604L8.65222 3.14661C8.55426 3.05274 8.42139 3 8.28285 3C8.14431 3 8.01145 3.05274 7.91349 3.14661C7.81553 3.24049 7.76049 3.36781 7.76049 3.50057C7.76049 3.63333 7.81553 3.76065 7.91349 3.85452L12.2405 8Z"
                fill="white"
              />
            </svg>
          </div>
        </div>
        <div style="display: flex; align-items: center; flex-direction: column;">
          <div style="font-size:10px;" >DATE</div>
          <div style="padding-top:8px;">
            ${dateAndTimeConverter(timestamp, "secondDate")}
          </div>
        </div>
      </div>
    </div>`;
};
